import type { ProjectState, TeamInfo } from "@livcg/schema";
import { Minus, Plus } from "lucide-react";
import { sendRaw } from "../../shared/sendRaw.js";
import { Button } from "../../ui/index.js";
import { useGoalFlash } from "../overlayReturn.js";
import { TeamColorSwatch } from "../TeamColorSwatch.js";
import { Panel } from "./Panel.js";

type Props = { slug: string; state: ProjectState };

export function ScoreControl({ slug, state }: Props) {
  const { flash } = useGoalFlash(slug);
  const adjust = (side: "home" | "away", delta: number) => {
    void sendRaw("score.adjust", { slug, side, delta });
    // SKORE parity: pressing + also puts the GOL tape on air (5s, then the previous mode).
    if (delta > 0) flash(side);
  };

  return (
    <Panel label="Hisob">
      <div className="flex flex-col gap-2">
        <ScoreRow
          slug={slug}
          side="home"
          team={state.teams.home}
          fallback={{ name: "Home", short: "HOM", color: "#5e6ad2" }}
          score={state.match.homeScore}
          onAdjust={(d) => adjust("home", d)}
        />
        <ScoreRow
          slug={slug}
          side="away"
          team={state.teams.away}
          fallback={{ name: "Away", short: "AWY", color: "#ef4444" }}
          score={state.match.awayScore}
          onAdjust={(d) => adjust("away", d)}
        />
      </div>
    </Panel>
  );
}

function ScoreRow({
  slug,
  side,
  team,
  fallback,
  score,
  onAdjust,
}: {
  slug: string;
  side: "home" | "away";
  team: TeamInfo | null;
  fallback: { name: string; short: string; color: string };
  score: number;
  onAdjust: (delta: number) => void;
}) {
  const name = team?.name ?? fallback.name;
  const color = team?.colorPrimary ?? fallback.color;
  return (
    <div className="flex items-center gap-2.5 rounded-md border border-divider bg-surface-muted/40 px-2.5 py-2">
      <TeamColorSwatch slug={slug} side={side} color={color} editable={!!team} className="h-7 w-7" />
      <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-ink">{name}</span>
      <Button
        variant="secondary"
        size="lg"
        iconOnly
        aria-label={`${name} hisobini −1`}
        onClick={() => onAdjust(-1)}
      >
        <Minus className="h-4 w-4" />
      </Button>
      <span className="w-9 text-center font-display text-[26px] font-bold tabular-nums leading-none text-ink">
        {score}
      </span>
      <Button
        variant="secondary"
        size="lg"
        iconOnly
        aria-label={`${name} hisobini +1`}
        onClick={() => onAdjust(+1)}
      >
        <Plus className="h-4 w-4" />
      </Button>
    </div>
  );
}
